import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ShieldAlert } from "lucide-react";
import { fadeUp } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "mt-2 w-full rounded-xl border border-white/15 bg-[#13111A] px-4 py-3 text-sm text-[#F3F1ED] placeholder:text-white/30 focus:border-[#F3F1ED] focus:outline-none";

export default function RhythmSubmit() {
  const [form, setForm] = useState({
    artist_name: "",
    email: "",
    project_title: "",
    release_date: "",
    streaming_link: "",
    epk_link: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/rhythm-submit`, form);
      toast.success(data.message || "Submission received.");
      setForm({
        artist_name: "",
        email: "",
        project_title: "",
        release_date: "",
        streaming_link: "",
        epk_link: "",
        notes: "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="submit" className="mx-auto max-w-7xl px-5 pt-16 sm:pt-24">
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="rhythm-card grid gap-10 rounded-[28px] p-7 sm:p-10 lg:grid-cols-2 lg:gap-14"
      >
        <div>
          <p className="font-rhythm-mono text-[11px] uppercase tracking-[0.22em] text-[#F3F1ED]">
            For artists, managers, publicists &amp; labels
          </p>
          <h2 className="mt-3 font-rhythm-display text-2xl font-extrabold uppercase tracking-tight sm:text-3xl">
            Submit Music
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-white/70">
            The Rhythm Report listens to everything — every genre, every level. Singles,
            albums, EPs, loosies from the vault. If it's real music, it gets a fair
            spin from Morgan Chanel.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-white/70">
            <li className="flex gap-3">
              <span className="text-[#F3F1ED]">01.</span> Private streaming links are
              preferred (SoundCloud, Dropbox, Streamable).
            </li>
            <li className="flex gap-3">
              <span className="text-[#F3F1ED]">02.</span> Include your release date —
              advance listens get first consideration.
            </li>
            <li className="flex gap-3">
              <span className="text-[#F3F1ED]">03.</span> EPK or press materials help,
              but the music decides.
            </li>
          </ul>
          <div className="mt-8 flex gap-3 rounded-2xl border border-white/10 bg-[#0B0A0F]/60 p-5">
            <ShieldAlert className="h-5 w-5 shrink-0 text-[#F3F1ED]" />
            <p className="text-xs leading-relaxed text-white/55">
              Submissions do not guarantee coverage or a positive review. Reviews are
              unfiltered and independent — that's the whole point.
            </p>
          </div>
        </div>

        <form data-testid="submit-music-form" onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">
              Artist name
            </label>
            <input
              data-testid="submit-artist-input"
              className={FIELD}
              value={form.artist_name}
              onChange={set("artist_name")}
              placeholder="Who's making the noise?"
              required
            />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">
              Contact email
            </label>
            <input
              data-testid="submit-email-input"
              type="email"
              className={FIELD}
              value={form.email}
              onChange={set("email")}
              placeholder="you@example.com"
              required
            />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">
              Project title
            </label>
            <input
              data-testid="submit-project-input"
              className={FIELD}
              value={form.project_title}
              onChange={set("project_title")}
              placeholder="Single, album or EP"
              required
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">
                Release date
              </label>
              <input
                data-testid="submit-date-input"
                className={FIELD}
                value={form.release_date}
                onChange={set("release_date")}
                placeholder="Oct 24, 2026"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">
                Private streaming link
              </label>
              <input
                data-testid="submit-link-input"
                className={FIELD}
                value={form.streaming_link}
                onChange={set("streaming_link")}
                placeholder="SoundCloud / Dropbox"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">
              EPK / press materials
            </label>
            <input
              data-testid="submit-epk-input"
              className={FIELD}
              value={form.epk_link}
              onChange={set("epk_link")}
              placeholder="Link to your EPK or press kit"
            />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">
              Anything else
            </label>
            <textarea
              data-testid="submit-notes-input"
              className={`${FIELD} min-h-[96px]`}
              value={form.notes}
              onChange={set("notes")}
              placeholder="The story behind the project..."
            />
          </div>
          <button
            data-testid="submit-music-form-submit-button"
            disabled={busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#F3F1ED] px-7 py-4 text-xs font-bold uppercase tracking-[0.18em] text-white transition-opacity hover:opacity-90 disabled:opacity-50 sm:w-auto"
          >
            {busy ? "Sending..." : "Send submission"} <ArrowRight className="h-4 w-4" />
          </button>
        </form>
      </motion.div>
    </section>
  );
}
